import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsKpi,
  CampaignAnalyticsTrendPoint,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { PerformancePanel } from "@/components/shared/performance/PerformancePanel";
import {
  DEFAULT_PERFORMANCE_SLOTS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
} from "@/components/shared/performance/performance";
import { useAttributionMix } from "@/hooks/use-attribution-note";
import { useShopScope } from "@/hooks/use-shop-scope";

interface PortfolioPerformancePanelProps {
  kpis: CampaignAnalyticsKpi[];
  trend: CampaignAnalyticsTrendPoint[];
  dataThrough: string | null;
  metric: PerformanceMetric;
  granularity: CampaignAnalyticsGranularity;
  onMetricChange: (metric: PerformanceMetric) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Shops behind the numbers; defaults to the analytics scope (the list page). */
  shopIds?: readonly string[];
}

/**
 * ADP-121: portfolio wrapper around the shared PerformancePanel. The KPI/trend
 * atoms are the campaign-analytics read model verbatim; the only portfolio
 * specifics are localized metric names (backend `kpi.label` is English-only,
 * the `portfolios:columns` namespace already localizes every metric) and the
 * portfolio pages' own slot/collapse persistence.
 */
export function PortfolioPerformancePanel({
  shopIds,
  ...props
}: PortfolioPerformancePanelProps): React.ReactElement {
  const { t } = useTranslation("portfolios");
  const { scopeShopIds } = useShopScope();
  const attributionMix = useAttributionMix(shopIds ?? scopeShopIds);
  return (
    <PerformancePanel
      metrics={PERFORMANCE_METRICS}
      defaultSlots={DEFAULT_PERFORMANCE_SLOTS}
      attributionMix={attributionMix}
      {...props}
      collapseStorageKey="adpilot:portfolio-performance:v1"
      slotsStorageKey="adpilot:portfolio-performance-slots:v1"
      getMetricLabel={(key) => t(`columns.${key}`)}
    />
  );
}
